import { Category, PaymentStatus, PREDICTABLE_SKU_TYPES, SkuTypes } from '../../interfaces/bbc'
import { BBCLogType } from '../../interfaces/bbcLog'
import { FilterFieldType, IFilter } from './types'

export const AR_INELIGIBLE_STATS_LABELS: Record<string, string> = {
  over_90DaysAndCreditsPastDue: 'Past Due & Credits',
  crossAge: 'Cross Age',
  ineligibleDebtors: 'Ineligible Customers',
  contras: 'Contras',
  concentration: 'Concentration',
  creditLimits: 'Credit Limits',
  arOtherMiscReserves: 'Other',
  ineligibleInvoices: 'Ineligible Invoices',
}

export const INVENTORY_DISTRIBUTION_FILTERS = [
  {
    field: 'brandCategory',
    dbField: 'brand_category',
    type: FilterFieldType.Autocomplete,
    title: 'Category',
    placeholder: 'Category',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'industrySubcategory',
    dbField: 'industry_subcategory',
    type: FilterFieldType.Autocomplete,
    title: 'Sub category',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'color',
    type: FilterFieldType.Autocomplete,
    title: 'Color',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'flavor',
    type: FilterFieldType.Autocomplete,
    title: 'Flavor',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'size',
    type: FilterFieldType.Autocomplete,
    title: 'Size',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'style',
    type: FilterFieldType.Autocomplete,
    title: 'Style',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'season',
    type: FilterFieldType.Autocomplete,
    title: 'Season',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'type',
    type: FilterFieldType.Autocomplete,
    title: 'Type',
    excludeColumn: true,
    isMultiple: true,
  },
  ...['custom_1', 'custom_2', 'custom_3', 'custom_4', 'custom_5'].map((customField): IFilter => ({
    field: customField,
    type: FilterFieldType.Autocomplete,
    title: customField.replace('_', ' ').replace('custom', 'Custom'),
    excludeColumn: true,
    isMultiple: true,
  })),
  {
    field: 'dwightCategory',
    dbField: 'dwight_category',
    type: FilterFieldType.Autocomplete,
    title: 'Standard category',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'dwightSubcategory',
    dbField: 'dwight_subcategory',
    type: FilterFieldType.Autocomplete,
    title: 'Standard sub category',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'dwightSize',
    dbField: 'dwight_size',
    type: FilterFieldType.Autocomplete,
    title: 'Standard size',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'dwightDepartment',
    dbField: 'dwight_department',
    type: FilterFieldType.Autocomplete,
    title: 'Standard department',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'department',
    dbField: 'department',
    type: FilterFieldType.Autocomplete,
    title: 'Department',
    excludeColumn: true,
    isMultiple: true,
  },
] as const satisfies IFilter[]
export const BBC_AR_INELIGIBILITY_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Customer',
    title: 'Customer',
    alias: 'debtorClientEligibility',
  },
  {
    field: 'eligible',
    dbField: 'eligible',
    alias: 'debtorClientEligibility',
    type: FilterFieldType.Boolean,
    isSortable: true,
    title: 'Eligible',
    options: [
      {
        value: 'true',
        label: 'Yes',
      },
      {
        value: 'false',
        label: 'No',
      },
    ],
  },
  {
    field: 'precedent',
    type: FilterFieldType.Empty,
    title: 'Suggestion',
    align: 'left',
  },
  {
    field: 'category',
    dbField: 'category',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      ...Object.keys(Category).map((category) => ({
        value: category,
        label: category,
      })),
    ],
    title: 'Category',
    alias: 'debtorClientEligibility',
  },
  {
    field: 'creditLimit',
    dbField: 'credit_limits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credit Limit',
  },
  {
    field: 'concentrationLimit',
    dbField: 'concentration_limit',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Concentr.Limit',
  },
  {
    field: 'totalIneligible',
    dbField: 'ineligible_amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Ineligible',
    alias: 'arIneligibility',
  },
  {
    field: 'summaryTotal',
    dbField: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
    alias: 'summary',
  },
  {
    field: 'notesReason',
    dbField: 'notes_reason',
    alias: 'debtorClientEligibility',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
  },

  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'New Customers',
        filters: {
          eligible: '',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BBC_MASTER_INVENTORY_INELIGIBILITY_LIST_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'First Appeared',
    title: 'First Appeared',
    alias: 'borrowingBase',
  },
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
    alias: 'masterInventory',
  },
  {
    field: 'sku',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'SKU',
    title: 'SKU',
    alias: 'masterInventory',
  },
  {
    field: 'location',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Location',
    title: 'Location',
  },
  {
    field: 'totalValue',
    type: FilterFieldType.Amount,
    isSortable: true,
    isCustom: true,
    placeholder: 'SKU',
    title: 'Total Amount',
    excludeFullSearchFilter: true,
  },
  {
    field: 'type',
    type: FilterFieldType.List,
    dbField: 'type',
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: '',
        label: 'New',
      },
      {
        value: SkuTypes.Finished,
        label: SkuTypes.Finished,
      },
      {
        value: SkuTypes.InProcess,
        label: SkuTypes.InProcess,
      },
      {
        value: SkuTypes.Raw,
        label: SkuTypes.Raw,
      },
      {
        value: SkuTypes.Packaging,
        label: SkuTypes.Packaging,
      },
      {
        value: SkuTypes.Other,
        label: SkuTypes.Other,
      },
    ],
    title: 'Type',
    alias: 'masterInventory',
    isMultiple: true,
  },
  {
    field: 'brandCategory',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Category',
    align: 'left',
    dbField: 'brand_category',
    alias: 'masterInventory',
  },
  {
    field: 'eligible',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
    alias: 'masterInventory',
  },
  {
    field: 'notes',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
    alias: 'masterInventory',
  },
  {
    // Filter-only: the suggestion is rendered inside the Type cell, not in a column of its own,
    // so this exists purely to isolate the review queue. `excludeColumn` is what keeps the
    // header and the body aligned.
    field: 'predictedType',
    dbField: 'predicted_type',
    type: FilterFieldType.List,
    excludeColumn: true,
    isMultiple: true,
    options: PREDICTABLE_SKU_TYPES.map((type) => ({ value: type, label: type })),
    title: 'Suggested type',
    placeholder: 'Suggested type',
    alias: 'masterInventory',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'New',
        filters: {
          type: [''],
        },
      },
      {
        title: 'Suggested',
        filters: {
          predictedType: [...PREDICTABLE_SKU_TYPES],
        },
      },
      {
        title: 'Finished Goods',
        filters: {
          type: [SkuTypes.Finished],
        },
      },
      {
        title: 'Not Finished Goods',
        filters: {
          type: ['', SkuTypes.InProcess, SkuTypes.Raw, SkuTypes.Packaging, SkuTypes.Other],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_INELIGIBILITY_SHORT_LIST_FILTERS_CONFIG = [
  {
    field: 'value',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Value',
    title: 'Value',
  },
  {
    field: 'totalValue',
    type: FilterFieldType.Number,
    isSortable: true,
    placeholder: 'Total Value',
    title: 'Total Value',
    dbField: 'COALESCE(total_values.total_value, 0)',
  },
  {
    field: 'eligible',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
  },
] as const satisfies IFilter[]

export const IS_BANK_ELIGBLE_FIELD: IFilter = {
  field: 'isBankEligible',
  dbField: 'is_bank_eligible',
  title: 'Bank Eligible',
  type: FilterFieldType.Boolean,
  isSortable: true,
}

export const BBC_INVENTORY_INELIGIBILITY_LOCATION_SHORT_LIST_FILTERS_CONFIG = [
  {
    field: 'value',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Value',
    title: 'Value',
  },
  {
    field: 'totalValue',
    type: FilterFieldType.Number,
    isSortable: true,
    placeholder: 'Total Value',
    title: 'Total Value',
    dbField: 'COALESCE(total_values.total_value, 0)',
  },
  {
    field: 'eligible',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
  },
  {
    field: 'mappedLocation',
    dbField: 'mapped_location',
    alias: 'inventoryLocation',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Alias',
  },
  {
    field: 'entityName',
    dbField: 'entity_name',
    alias: 'inventoryLocation',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Vendor',
  },
  {
    field: 'foreignCountry',
    dbField: 'foreign_country',
    title: 'Foreign Country',
    type: FilterFieldType.List,
    isSortable: true,
    options: [],
  },
  IS_BANK_ELIGBLE_FIELD,
  {
    field: 'warehouseWaiverLink',
    dbField: 'warehouse_waiver_link',
    type: FilterFieldType.Empty,
    title: 'Waiver',
    isSortable: true,
    alias: 'inventoryLocation',
  },
] as const satisfies IFilter[]

export const BBC_RECEIVABLES_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    dbField: 'memo',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Customer',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Invoice #',
    title: 'Invoice #',
  },
  {
    field: 'balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Balance',
  },
  {
    field: 'invoiceDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Invoice Date',
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Days Past Invoice',
  },
] as const satisfies IFilter[]

export const BBC_PAYABLES_LIST_FILTERS_CONFIG = [
  {
    field: 'creditor',
    dbField: 'memo',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'mapping',
    type: FilterFieldType.Empty,
    title: 'Vendor',
    align: 'left',
  },
  {
    field: 'prediction',
    type: FilterFieldType.Empty,
    title: 'Prediction',
    align: 'left',
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Invoice #',
    title: 'Invoice #',
  },
  {
    field: 'balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Balance',
  },
  {
    field: 'invoiceDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Invoice Date',
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Days Past Invoice',
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_INELIGIBLE_ANALYSIS_SKU_LIST_FILTERS_CONFIG = [
  {
    field: 'description',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'sku',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'SKU',
    title: 'SKU',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Type',
    title: 'Type',
  },
  {
    field: 'location',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Location',
    title: 'Location',
  },
  {
    field: 'eligible',
    title: 'Eligibility',
    type: FilterFieldType.List,
    isSortable: true,
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: true,
        label: 'Eligible',
      },
      {
        value: false,
        label: 'Ineligible',
      },
    ],
  },
  {
    field: 'weeksOfSupply',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Weeks of Supply',
  },
  {
    field: 'totalValue',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Value',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_INELIGIBLE_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.Empty,
    title: 'Category',
    placeholder: 'Category',
    isSortable: true,
    align: 'left',
  },
  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_DISTRIBUTION_LIST_FILTERS_CONFIG = [
  {
    field: 'distribution',
    type: FilterFieldType.Empty,
    title: 'Category',
    placeholder: 'Category',
    isSortable: true,
    align: 'left',
  },
  ...INVENTORY_DISTRIBUTION_FILTERS,
  {
    field: 'location',
    dbField: 'location',
    type: FilterFieldType.Autocomplete,
    title: 'Location',
    excludeColumn: true,
    isMultiple: true,
  },
  {
    field: 'value_0',
    title: 'Total',
    type: FilterFieldType.Amount,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'percent_0',
    title: '% of Total',
    type: FilterFieldType.Percent,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },

  {
    field: 'value_1',
    title: 'Total',
    type: FilterFieldType.Amount,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'percent_1',
    title: '% of Total',
    type: FilterFieldType.Percent,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'value_2',
    title: 'Total',
    type: FilterFieldType.Amount,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'percent_2',
    title: '% of Total',
    type: FilterFieldType.Percent,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'value_3',
    title: 'Total',
    type: FilterFieldType.Amount,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'percent_3',
    title: '% of Total',
    type: FilterFieldType.Percent,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'value_4',
    title: 'Total',
    type: FilterFieldType.Amount,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'percent_4',
    title: '% of Total',
    type: FilterFieldType.Percent,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
] as const satisfies IFilter[]

/**
 * The aging exhibits reuse the distribution filters and add the Finished Goods quick filter,
 * matching the Sales by Type exhibit. Kept separate so the distribution table, which shares
 * the config above, does not grow a quick filter row it never had.
 */
export const INVENTORY_AGING_EXHIBIT_FILTERS_CONFIG = [
  ...BBC_INVENTORY_DISTRIBUTION_LIST_FILTERS_CONFIG,
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Finished Goods',
        filters: {
          type: [SkuTypes.Finished],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_INELIGIBLE_REASONS_LIST_FILTERS_CONFIG = [
  {
    field: 'reason',
    type: FilterFieldType.Text,
    title: 'Reason',
    isSortable: true,
  },
  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
] as const satisfies IFilter[]

export const BBC_INVENTORY_INELIGIBLE_DETAILS_LIST_FILTERS_CONFIG = [
  {
    field: 'sku',
    type: FilterFieldType.Text,
    title: 'SKU',
    isSortable: true,
  },

  {
    field: 'description',
    type: FilterFieldType.Text,
    title: 'Description',
    placeholder: 'Description',
    isSortable: true,
  },

  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isHighlighted: true,
    isSortable: true,
    align: 'right',
  },
] as const satisfies IFilter[]

export const BBC_INELIGIBLE_AR_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.Autocomplete,
  },
  {
    field: 'debtor',
    type: FilterFieldType.Text,
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
  },
  {
    field: 'value_0',
    type: FilterFieldType.Amount,
  },
  {
    field: 'value_1',
    type: FilterFieldType.Amount,
  },
  {
    field: 'value_2',
    type: FilterFieldType.Amount,
  },
  {
    field: 'value_3',
    type: FilterFieldType.Amount,
  },
  {
    field: 'value_4',
    type: FilterFieldType.Amount,
  },
] as const satisfies IFilter[]

export const BBC_AR_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Customer',
    title: 'Customer',
    isMultiple: true,
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    align: 'right',
    placeholder: 'Invoice',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    title: 'Age',
    align: 'right',
    placeholder: 'Age',
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'ar_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
    alias: 'current',
  },
  {
    field: 'ar_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
    alias: 'current',
  },
  {
    field: 'ar_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
    alias: 'current',
  },
  {
    field: 'ar_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
    alias: 'current',
  },
  {
    field: 'ineligible',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Ineligible',
    alias: 'current',
  },
  {
    field: 'eligible',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Eligible',
    alias: 'current',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
    alias: 'current',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
  {
    title: 'AR Status',
    field: 'category',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    alias: 'debtor_client_eligibility',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [],
  },
] as const satisfies IFilter[]

export const BBC_AR_SUMMARY_DETAILS_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Text,
    title: 'Customer',
    placeholder: 'Customer',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'memo',
    type: FilterFieldType.Text,
    title: 'Customer',
    placeholder: 'Customer',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    placeholder: 'Invoice',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    title: 'Age',
    placeholder: 'Age',
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'ar_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
    alias: 'current',
  },
  {
    field: 'ar_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
    alias: 'current',
  },
  {
    field: 'ar_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
    alias: 'current',
  },
  {
    field: 'ar_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
    alias: 'current',
  },
  {
    field: 'ineligible',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Ineligible',
    alias: 'current',
  },
  {
    field: 'eligible',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total Eligible',
    alias: 'current',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
    alias: 'current',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
] as const satisfies IFilter[]

export const BBC_AP_SUMMARY_LIST_FILTERS_CONFIG = [
  {
    field: 'creditor',
    type: FilterFieldType.Autocomplete,
    placeholder: 'Vendor',
    isMultiple: true,
    title: 'Vendor',
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    align: 'right',
    placeholder: 'Invoice',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    title: 'Age',
    align: 'right',
    placeholder: 'Age',
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'ap_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
    alias: 'current',
  },
  {
    field: 'ap_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
    alias: 'current',
  },
  {
    field: 'ap_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
    alias: 'current',
  },
  {
    field: 'ap_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
    alias: 'current',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AP',
    alias: 'current',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'isPriorityPayable',
        type: FilterFieldType.Text,
      },
    ],
    quickFilters: [
      {
        title: 'Priority Vendors',
        isHidden: true,
        filters: {
          isPriorityPayable: 'true',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BBC_AP_SUMMARY_DETAILS_LIST_FILTERS_CONFIG = [
  {
    field: 'memo',
    type: FilterFieldType.Text,
    title: 'Vendor',
    placeholder: 'Vendor',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },

  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    isSortable: true,
    alias: 'current',
    excludeFullSearchFilter: true,
  },
  {
    field: 'daysPastInvoice',
    type: FilterFieldType.Number,
    title: 'Age',
    isSortable: true,
    alias: 'current',
  },
  {
    field: 'ap_1To_30Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '1-30 Days',
    alias: 'current',
  },
  {
    field: 'ap_31To_60Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '31-60 Days',
    alias: 'current',
  },
  {
    field: 'ap_61To_90Days',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '61-90 Days',
    alias: 'current',
  },
  {
    field: 'ap_91PlusDays',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: '91+ Days',
    alias: 'current',
  },
  {
    field: 'total',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AP',
    alias: 'current',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    excludeFullSearchFilter: true,
    isCustom: true,
  },
] as const satisfies IFilter[]

export const BBC_AP_ROLLFORWARD_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.Text,
    title: 'Vendor',
    align: 'left',
  },
  {
    field: 'newBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AP',
    isHighlighted: true,
    highlightColor: 'primary',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    isHighlighted: true,
    highlightColor: 'primary',
  },
  {
    field: 'newBills',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Bills',
    isHighlighted: true,
  },
  {
    field: 'impliedPayments',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Payments',
    isHighlighted: true,
  },
  {
    field: 'creditsApplied',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credits Applied',
    isHighlighted: true,
  },
  {
    field: 'newCredits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Credits',
    isHighlighted: true,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    fields: [
      {
        field: 'isPriorityPayable',
        type: FilterFieldType.Text,
      },
    ],
    quickFilters: [
      {
        title: 'Priority Vendors',
        isHidden: true,
        filters: {
          isPriorityPayable: 'true',
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const BBC_AP_ROLLFORWARD_DETAIL_LIST_FILTERS_CONFIG = [
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    align: 'left',
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    field: 'newBills',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Bills',
  },
  {
    field: 'invoiceDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Invoice Date',
  },
  {
    field: 'impliedPayments',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Payments',
  },
  {
    field: 'paidDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Paid Date',
  },
  {
    field: 'creditsApplied',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credits Applied',
  },
  {
    field: 'newCredits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Credits',
  },
] as const satisfies IFilter[]

export const BBC_AR_ROLLFORWARD_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.Text,
    title: 'Customer',
    align: 'left',
  },
  {
    field: 'newBalance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Total AR',
    isHighlighted: true,
    highlightColor: 'primary',
  },
  {
    field: 'percent',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: '% of Total',
    isHighlighted: true,
    highlightColor: 'primary',
  },
  {
    field: 'newInvoices',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Invoices',
    isHighlighted: true,
  },
  {
    field: 'impliedCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Collections',
    isHighlighted: true,
  },
  {
    field: 'actualCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Actual Collections',
    isHighlighted: true,
  },
  {
    field: 'impliedReal',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied - Real',
    isHighlighted: true,
  },
  {
    field: 'impliedDilution',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Implied Dilution',
    isHighlighted: true,
  },
  {
    field: 'creditsApplied',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credits Applied',
    isHighlighted: true,
  },
  {
    field: 'newCredits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Credits',
    isHighlighted: true,
  },
  {
    title: 'AR Status',
    field: 'category',
    type: FilterFieldType.List,
    isMultiple: true,
    excludeColumn: true,
    alias: 'debtor_client_eligibility',
    options: [],
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [],
  },
] as const satisfies IFilter[]

export const BBC_AR_ROLLFORWARD_DETAIL_LIST_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Text,
    title: 'Customer',
    align: 'left',
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    align: 'left',
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    field: 'newInvoices',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Invoices',
  },
  {
    field: 'invoiceDate',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Invoice Date',
  },
  {
    field: 'impliedCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied Collections',
  },
  {
    field: 'paidDate',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Paid Date',
    dbField: 'lastAppearedDate',
  },
  {
    field: 'actualCollections',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Actual Collections',
  },
  {
    field: 'impliedReal',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Implied - Real',
  },
  {
    field: 'impliedDilution',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Implied Dilution',
  },
  {
    field: 'creditsApplied',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Credits Applied',
  },
  {
    field: 'newCredits',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'New Credits',
  },
] as const satisfies IFilter[]

export const BBC_DETAIL_LIST_FILTERS_CONFIG = [
  {
    field: 'label',
    dbField: 'label',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
  },
  {
    field: 'reference',
    dbField: 'reference',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Reference',
    title: 'Reference',
  },
  {
    field: 'originalDate',
    dbField: 'originalDate',
    type: FilterFieldType.Date,
    align: 'right',
    title: 'Original BBC date',
    isSortable: true,
  },
  {
    field: 'oldValue',
    dbField: 'oldValue',
    type: FilterFieldType.Text,
    title: 'Old value',
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'newValue',
    dbField: 'newValue',
    type: FilterFieldType.Text,
    title: 'New value',
    isSortable: true,
    align: 'right',
    excludeFilter: true,
  },
  {
    field: 'impactToAvailability',
    dbField: 'impact_to_availability',
    type: FilterFieldType.Amount,
    title: 'Availability impact',
    isSortable: true,
    align: 'right',
  },
] as const satisfies IFilter[]

export const BBC_DETAIL_DUPLICATE_INVOICES_FILTERS_CONFIG = [
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Invoice',
    title: 'Invoice',
    align: 'left',
  },
  {
    field: 'count',
    type: FilterFieldType.Number,
    dbField: 'COUNT(*)',
    isSortable: true,
    title: 'Count',
    align: 'right',
    excludeFullSearchFilter: true,
    aggColumn: true,
  },
  {
    field: 'customer',
    dbField: `STRING_AGG(distinct "alias"."linked_name", ', ')`,
    type: FilterFieldType.Text,
    align: 'left',
    title: 'Customer',
    isSortable: false,
    aggColumn: true,
  },
] as const satisfies IFilter[]

export const BBC_DETAIL_CONFIRMED_PRIORITY_INVOICE_FILTERS_CONFIG = [
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Invoice',
    title: 'Invoice',
    align: 'left',
  },
  {
    field: 'balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Amount',
    align: 'right',
  },
  {
    field: 'creditor',
    dbField: `STRING_AGG(distinct "alias"."linked_name", ', ')`,
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: '3PL',
    aggColumn: true,
  },
] as const satisfies IFilter[]

export const BBC_DETAIL_PAID_INVOICE_FILTERS_CONFIG = [
  {
    field: 'invoice',
    alias: 'masterInvoice',
    dbField: 'invoice_number',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Description',
    title: 'Description',
    align: 'left',
  },
  {
    field: 'paidAmount',
    dbField: 'ABS(masterInvoiceActivityCurrent.amount)',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Paid Amount',
    align: 'right',
  },
  {
    field: 'paidDate',
    alias: 'masterInvoiceActivityCurrent',
    dbField: 'record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Paid Date',
    align: 'left',
  },
  {
    field: 'debtor',
    alias: 'masterInvoice',
    dbField: 'debtor',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'Reference',
  },
] as const satisfies IFilter[]

export const BBC_DETAIL_ALTERED_DATE_FILTERS_CONFIG = [
  ...BBC_DETAIL_LIST_FILTERS_CONFIG,
  {
    field: 'message',
    type: FilterFieldType.Text,
    isSortable: true,
    align: 'left',
    title: 'Message',
  },
] as const satisfies IFilter[]

export const BBC_LOGS_LIST_FILTERS_CONFIG = [
  {
    field: 'type',
    alias: 'bbcLog',
    type: FilterFieldType.List,
    isMultiple: true,
    options: [
      {
        value: 'all',
      },
      ...Object.values(BBCLogType).map((value) => ({
        value,
        label: value,
      })),
    ],
    title: 'Type',
  },
  {
    field: 'userId',
    dbField: 'user_id',
    alias: 'users',
    type: FilterFieldType.Autocomplete,
    isMultiple: true,
    placeholder: 'User',
    title: 'User',
  },
  {
    field: 'createdAt',
    dbField: 'created_at',
    alias: 'bbcLog',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Created At',
  },
] as const satisfies IFilter[]
export const AR_INELIGIBLE_STATS_FILTERS_CONFIG = [
  {
    field: 'label',
    type: FilterFieldType.List,
    isMultiple: true,
    options: Object.keys(AR_INELIGIBLE_STATS_LABELS).map((label) => ({
      value: label,
      label: AR_INELIGIBLE_STATS_LABELS[label as keyof typeof AR_INELIGIBLE_STATS_LABELS] || label,
    })),
    placeholder: 'Reason',
    title: 'Reason',
    isSortable: true,
  },
  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: 'Current BBC',
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
] as const satisfies IFilter[]

export const AR_INELIGIBLE_STATS_DETAILS_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Text,
    title: 'Customer',
    isSortable: true,
  },
  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: 'Current BBC',
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
] as const satisfies IFilter[]

export const AR_INELIGIBLE_STATS_DETAILS_INVOICES_FILTERS_CONFIG = [
  {
    field: 'debtor',
    type: FilterFieldType.Text,
    title: 'Customer',
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    field: 'invoice',
    type: FilterFieldType.Text,
    title: 'Invoice',
    isSortable: true,
    excludeFullSearchFilter: true,
  },
  {
    field: 'value_0',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: 'Current BBC',
    align: 'right',
  },
  {
    field: 'value_1',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_2',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_3',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
  {
    field: 'value_4',
    type: FilterFieldType.Empty,
    isSortable: true,
    title: '',
    align: 'right',
  },
] as const satisfies IFilter[]

export const MASTER_INVOICES_LIST_FILTERS_CONFIG = [
  {
    field: 'invoiceNumber',
    dbField: 'invoice_number',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Invoice #',
    title: 'Invoice #',
  },
  {
    field: 'debtor',
    dbField: 'debtor',
    alias: 'masterInvoice',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Debtor',
    title: 'Debtor',
    excludeFullSearchFilter: true,
  },
  {
    field: 'invoiceDate',
    dbField: 'invoice_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'Invoice Date',
    title: 'Invoice Date',
  },
  {
    field: 'dueDate',
    dbField: 'due_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'Due Date',
    title: 'Due Date',
  },
  {
    field: 'firstAppearedDate',
    dbField: 'firstBbc.record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'First Appeared',
    title: 'First Appeared',
    align: 'right',
    excludeFullSearchFilter: true,
  },
  {
    field: 'lastAppearedDate',
    dbField: 'lastBbc.record_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'Last Appeared',
    title: 'Last Appeared',
    align: 'right',
    excludeFullSearchFilter: true,
  },
  {
    field: 'paidDate',
    dbField: 'paid_date',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'Paid Date',
    title: 'Paid Date',
    align: 'right',
    isCustom: true,
  },
  {
    field: 'originalAmount',
    dbField: 'original_amount',
    type: FilterFieldType.Amount,
    isSortable: true,
    placeholder: 'Original Amount',
    title: 'Original Amount',
    align: 'right',
  },
  {
    field: 'impliedBalance',
    dbField: 'implied_balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    placeholder: 'Implied Balance',
    title: 'Implied Balance',
    align: 'right',
    isCustom: true,
  },
  {
    field: 'wireBalance',
    dbField: 'wire_balance',
    type: FilterFieldType.Amount,
    isSortable: true,
    placeholder: 'Wire Balance',
    title: 'Wire Balance',
    align: 'right',
    isCustom: true,
  },
  {
    field: 'eligible',
    type: FilterFieldType.Boolean,
    isSortable: true,
    align: 'right',
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: 'true',
        label: 'Eligible',
      },
      {
        value: 'false',
        label: 'Ineligible',
      },
    ],
    title: 'Eligible',
  },
  {
    field: 'paymentStatus',
    dbField: 'payment_status',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    align: 'right',
    options: [
      {
        value: 'all',
        label: 'All',
      },
      {
        value: PaymentStatus.Paid,
        label: PaymentStatus.Paid,
      },
      {
        value: PaymentStatus.ImpliedPaid,
        label: PaymentStatus.ImpliedPaid,
      },
      {
        value: PaymentStatus.PartiallyPaid,
        label: PaymentStatus.PartiallyPaid,
      },
      {
        value: PaymentStatus.Unpaid,
        label: PaymentStatus.Unpaid,
      },
    ],
    title: 'Payment Status',
  },
  {
    field: 'notes',
    dbField: 'notes',
    type: FilterFieldType.Text,
    isSortable: true,
    placeholder: 'Notes',
    title: 'Notes',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Eligible',
        filters: {
          eligible: ['true'],
        },
      },
      {
        title: 'Ineligible',
        filters: {
          eligible: ['false'],
        },
      },
      {
        title: 'Open',
        filters: {
          paymentStatus: [PaymentStatus.Unpaid, PaymentStatus.PartiallyPaid],
        },
      },
      {
        title: PaymentStatus.Paid,
        filters: {
          paymentStatus: [PaymentStatus.Paid, PaymentStatus.ImpliedPaid],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const MASTER_INVOICE_BBC_HISTORY_FILTERS_CONFIG = [
  {
    field: 'recordDate',
    dbField: 'record_date',
    alias: 'bb',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'BBC Date',
    align: 'left',
  },
  {
    field: 'invoiceDate',
    dbField: 'invoice_date',
    alias: 'car',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Invoice Date',
    align: 'left',
  },
  {
    field: 'balance',
    alias: 'car',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Balance',
    align: 'right',
  },
  {
    field: 'eligible',
    alias: 'car',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Eligible',
    align: 'right',
  },
] as const satisfies IFilter[]
